import { useCallback, useRef, useState } from "react"

import gsap from "gsap"
import { Trans } from "react-i18next"
import styled, { css } from "styled-components"

import RoundButton from "components/buttons/RoundButton"
import InnerWrapper from "components/InnerWrapper"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"

import YouTubePlayer from "./YoutubeIframe"

interface Props {
  data: Queries.MainPageQuery["allContentfulMainPage"]["nodes"][number]["videoCarousel"]
  justVideo?: boolean
  className?: string
}

export default function Testimonials({
  data,
  justVideo = false,
  className = "",
}: Props) {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const activeTestimonial = useRef(0)
  const canAnimate = useRef(true)
  const [index, setIndex] = useState(0)
  const innerWidth = useMedia("1402px", "84.458vw", "100%", "80%")
  const [transitioning, setTransitioning] = useState(false)

  const items = data?.videoEmbeds

  const animateCarouselForward = useCallback(
    (
      leftTestimonial: number,
      centerTestimonial: number,
      rightTestimonial: number
    ) => {
      if (wrapper) {
        const tl = gsap.timeline({
          onStart: () => {
            canAnimate.current = false
            setTransitioning(true)
          },
          onComplete: () => {
            canAnimate.current = true
            setTransitioning(false)
          },
        })
        activeTestimonial.current = leftTestimonial
        // Set positions for all slides except the current visible ones
        items?.forEach((_:any, index:number) => {
          if (
            index !== leftTestimonial &&
            index !== centerTestimonial &&
            index !== rightTestimonial
          ) {
            gsap.set(`.testimonial-${index}`, { x: "100%", opacity: 0 });
          }
          });
        gsap.set(`.testimonial-${leftTestimonial}`, { x: "100%" })
        tl.to(`.testimonial-${leftTestimonial}`, {
            x: 0,
            ease: "power2.inOut",
            opacity: 1,
            duration: 0.4,
          }, 0)
          .to(`.testimonial-${centerTestimonial}`, {
              x: "-100%",
              duration: 0.5,
              opacity: 0,
              ease: "power2.inOut",
            }, 0)

          .to(`.testimonial-${rightTestimonial}`, {
              x: `100%`,
              duration: 0,
              ease: "power2.inOut",
            }, 0)
      }
    },
    [wrapper, items]
  )

  const animateCarouselBack = useCallback(
    (
      leftTestimonial: number,
      centerTestimonial: number,
      rightTestimonial: number
    ) => {
      if (wrapper) {
        const tl = gsap.timeline({
          onStart: () => {
            canAnimate.current = false
            setTransitioning(true)
          },
          onComplete: () => {
            canAnimate.current = true
            setTransitioning(false)
          },
        })
        activeTestimonial.current = rightTestimonial
        gsap.set(`.testimonial-${rightTestimonial}`, { x: "-100%" })
        tl.to(`.testimonial-${leftTestimonial}`, {
            x: "-100%",
            ease: "power2.inOut",
            duration: 0,
          }, 0)
          .to(`.testimonial-${centerTestimonial}`, {
              x: "100%",
              duration: 0.5,
              opacity: 0,
              ease: "power2.inOut",
            }, 0)

          .to(`.testimonial-${rightTestimonial}`, {
              x: 0,
              duration: 0.5,
              opacity: 1,
              ease: "power2.inOut",
            }, 0)
      }
    },
    [wrapper]
  )

  const handleNextItem = (currentTestimonial: number) => {
    if (canAnimate.current && items) {
      const leftTestimonial =
        currentTestimonial === items.length - 1 ? 0 : currentTestimonial + 1
      const rightTestimonial =
        currentTestimonial === 0 ? items.length - 1 : currentTestimonial - 1

      animateCarouselForward(
        leftTestimonial,
        currentTestimonial,
        rightTestimonial
      )
      setIndex(leftTestimonial)
    }
  }

  const handlePrevItem = (currentTestimonial: number) => {
    if (canAnimate.current && items) {
      const leftTestimonial =
        currentTestimonial === items.length - 1 ? 0 : currentTestimonial + 1
      const rightTestimonial =
        currentTestimonial === 0 ? items.length - 1 : currentTestimonial - 1
      animateCarouselBack(leftTestimonial, currentTestimonial, rightTestimonial)
      setIndex(rightTestimonial)
    }
  }

  const allTitles = items?.map((item, i) => {
    return (
      <Title active={index === i} key={item?.contentful_id}>
        <Trans>{item?.videoName}</Trans>
      </Title>
    )
  })

  const allItems = items?.map((item, i) => {        
    return (
      <YouTubePlayer
        key={item?.contentful_id}
        className={`testimonial-${i}`}
        iframeData={item}
        first={i === 0}
        transitioning={transitioning}
      />
    )
  })

  const dots = items?.map((item, i) => {
    return (
      <Dot viewBox="0 0 10 10" key={item?.contentful_id}>
        <circle
          cx="10"
          cy="10"
          r="5"
          className={`circle-${i}`}
          fill={index === i ? colors.blue400 : colors.blue10}
        />
      </Dot>
    )
  })

  return (
    <Wrapper
      ref={ref => {
        setWrapper(ref)
      }}
      justVideo={justVideo}
      className={className}
    >
      <Titles>{allTitles}</Titles>
      <StyledInnerWrapper className="vid-carousel-inner" setWidth={innerWidth}>
        {items && items.length > 1 && (
          <RoundButtonStyled
            left
            blue
            onClick={() => handlePrevItem(activeTestimonial.current)}
          />
        )}

        <CarouselWrapper>{allItems}</CarouselWrapper>
        {items && items.length > 1 && (
          <RoundButtonStyled2
            right
            blue
            onClick={() => handleNextItem(activeTestimonial.current)}
          />
        )}
      </StyledInnerWrapper>
      {items && items.length > 1 && <DotWrapper>{dots}</DotWrapper>}
    </Wrapper>
  )
}

const Title = styled.h3<{ active: boolean }>`
  position: absolute;
  text-align: center;
  width: 100%;
  align-self: center;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.4s ease-in-out;

  ${fresponsive(css`
    ${text.h4}
  `)}

  ${ftablet(css`
    ${text.h5}
  `)}

  ${fmobile(css`
    ${text.h6}
  `)}
`

const Dot = styled.svg`
  display: block;
  position: relative;

  circle {
    transition: fill 0.3s ease-in-out;
  }

  width: 10px;
  height: 10px;

  ${media.mobile} {
    width: 5px;
    height: 5px;
  }
`

const Wrapper = styled.section<{ justVideo: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  :before {
    content: "";
    position: absolute;
    display: ${({ justVideo }) => (justVideo ? "none" : "inline-block")};
    top: 0;
    left: 0;
    width: fit-content;
    height: 52.3%;
    // background: ${colors.blue10};
    z-index: 0;
  }

  ${media.fullWidth} {
    padding: ${({ justVideo }) => (justVideo ? "200px" : "100px")} 0 100px;
  }
  ${media.desktop} {
    padding: ${({ justVideo }) => (justVideo ? "12.048vw" : "6.024vw")} 0
      6.024vw;
  }
  ${media.tablet} {
    padding: ${({ justVideo }) => (justVideo ? "19.531vw" : "9.766vw")} 0
      16.113vw;
    :before {
      height: 53%;
    }
  }
  ${media.mobile} {
    padding: ${({ justVideo }) => (justVideo ? "40vw" : "20vw")} 0;
    :before {
      height: 46%;
    }
  }
`

const Titles = styled.div`
  position: relative;
  display: flex;

  ${fresponsive(css`
    width: 1144px;
    height: 116px;
    margin-bottom: 50px;
  `)}

  ${ftablet(css`
    width: 792px;
    height: 84px;
  `)}

  ${fmobile(css`
    width: 335px;
    height: 93px;
    margin-bottom: 25px;
  `)}
`

const StyledInnerWrapper = styled(InnerWrapper)`
  display: flex;
  justify-content: center;
  align-items: center;

  ${fresponsive(css`
    gap: 74px;
    width: 1402px;
  `)}

  ${ftablet(css`
    width: 834px;
    gap: 0;
  `)}

  ${fmobile(css`
    width: 335px;
  `)}
`

const RoundButtonStyled = styled(RoundButton)`
  z-index: 2;

  ${media.tablet} {
    position: absolute;
    left: -27px;
  }
  ${media.mobile} {
    position: absolute;
    left: 23vw;
    top: calc(100% + 16vw);
  }
`

const CarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;

  iframe {
    border: none;
    outline: none;
  }

  ${fresponsive(css`
    width: 1144px;
    height: 740px;
    border-radius: 20px;
    margin-bottom: 25px;
  `)}

  ${ftablet(css`
    width: 834px;
    height: 600px;
  `)}

  ${fmobile(css`
    width: 335px;
    height: 260px;
    border-radius: 10px;
  `)}
`

const RoundButtonStyled2 = styled(RoundButtonStyled)`
  ${media.tablet} {
    left: auto;
    right: -27px;
  }
  ${media.mobile} {
    left: auto;
    right: 23vw;
  }
`

const DotWrapper = styled.div`
  display: flex;

  width: fit-content;

  ${fresponsive(css`
    height: 10px;
    gap: 10px;
  `)}
`
